// src/views/dashboard/salesrep/IVRsModal.jsx
import React, { useState, useEffect } from "react";
import { IoCloseOutline, IoDocumentTextOutline, IoPersonOutline, IoFilter, IoCheckmarkCircle } from "react-icons/io5";
import { Loader2, FileText, TrendingUp, Filter, Sparkles } from "lucide-react";

const IVRsModal = ({ isOpen, onClose }) => {
  const [ivrs, setIvrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (isOpen) {
      fetchIVRs();
    }
  }, [isOpen, statusFilter]);

  const fetchIVRs = async () => {
    try {
      setLoading(true);
      const url = statusFilter === 'all' 
        ? 'https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/sales-rep/ivrs/'
        : `https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/sales-rep/ivrs/?status=${statusFilter}`;
        
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch IVRs');
      }

      const data = await response.json();
      setIvrs(data.data || []);
    } catch (err) {
      console.error('Error fetching IVRs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusGradient = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-lg';
      case 'denied':
        return 'bg-gradient-to-r from-red-400 to-rose-500 text-white shadow-lg';
      case 'pending':
        return 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white shadow-lg';
      case 'cancelled':
        return 'bg-gradient-to-r from-gray-400 to-slate-500 text-white shadow-lg';
      case 'withdrawn':
        return 'bg-gradient-to-r from-orange-400 to-amber-600 text-white shadow-lg';
      default:
        return 'bg-gradient-to-r from-gray-400 to-slate-500 text-white shadow-lg';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return '✓';
      case 'denied':
        return '✕';
      case 'pending':
        return '⏳';
      case 'cancelled':
        return '⊘';
      case 'withdrawn':
        return '↩';
      default:
        return '•';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto animate-fadeIn">
      {/* Enhanced Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-all duration-300"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-3xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden border border-gray-200 dark:border-gray-700 animate-scaleIn">
          
          {/* Ultra Modern Header */}
          <div className="relative bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 p-8 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            
            <div className="relative z-10 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center animate-bounce">
                  <FileText className="text-white" size={32} />
                </div>
                <div>
                  <h2 className="text-3xl font-black text-white drop-shadow-lg">
                    IVR Forms 📋
                  </h2>
                  <p className="text-white/90 font-medium mt-1 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                      <Sparkles size={16} />
                      {ivrs.length} form{ivrs.length !== 1 ? 's' : ''} submitted
                    </span>
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="group p-3 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-xl transition-all duration-300 hover:scale-110 hover:rotate-90"
              >
                <IoCloseOutline className="text-2xl text-white" />
              </button>
            </div>
          </div>

          {/* Filter Bar with Modern Design */}
          <div className="p-6 bg-gradient-to-r from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300 font-semibold">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Filter className="text-white" size={20} />
                </div>
                <span>Filter by status:</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {[
                  { value: 'all', label: 'All', gradient: 'from-gray-500 to-slate-600' },
                  { value: 'pending', label: 'Pending', gradient: 'from-yellow-500 to-amber-600' },
                  { value: 'approved', label: 'Approved', gradient: 'from-green-500 to-emerald-600' },
                  { value: 'denied', label: 'Denied', gradient: 'from-red-500 to-rose-600' },
                  { value: 'withdrawn', label: 'Withdrawn', gradient: 'from-orange-500 to-amber-600' }
                ].map((status) => (
                  <button
                    key={status.value}
                    onClick={() => setStatusFilter(status.value)}
                    className={`px-4 py-2 text-sm font-bold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-md ${
                      statusFilter === status.value
                        ? `bg-gradient-to-r ${status.gradient} text-white scale-105`
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:shadow-lg'
                    }`}
                  >
                    {status.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-280px)] custom-scrollbar">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="relative">
                  <div className="w-20 h-20 border-4 border-teal-200 dark:border-teal-800 rounded-full animate-pulse"></div>
                  <div className="absolute top-0 left-0 w-20 h-20 border-4 border-transparent border-t-teal-600 rounded-full animate-spin"></div>
                </div>
                <p className="mt-4 text-lg font-semibold text-gray-600 dark:text-gray-400 animate-pulse">
                  Loading IVR forms...
                </p>
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-gradient-to-br from-red-400 to-rose-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                  <span className="text-4xl">⚠️</span>
                </div>
                <p className="text-xl font-bold text-red-600 dark:text-red-400 mb-2">Oops! Something went wrong</p>
                <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
                <button 
                  onClick={fetchIVRs}
                  className="px-6 py-3 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl font-bold hover:from-red-600 hover:to-rose-700 transform hover:scale-105 transition-all duration-200 shadow-lg"
                >
                  🔄 Try Again
                </button>
              </div>
            ) : ivrs.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <IoDocumentTextOutline className="text-5xl text-gray-400" />
                </div>
                <p className="text-xl font-bold text-gray-500 dark:text-gray-400 mb-2">
                  {statusFilter === 'all' 
                    ? 'No IVR forms submitted yet' 
                    : `No ${statusFilter} IVR forms found`}
                </p>
                <p className="text-gray-400 dark:text-gray-500">IVR submissions will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-700 dark:to-gray-800">
                      <th className="px-6 py-4 text-left text-xs font-black text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Patient
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-black text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Provider
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-black text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Wound Size
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-black text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-black text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Submitted
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-black text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800">
                    {ivrs.map((ivr, index) => (
                      <tr 
                        key={ivr.id} 
                        className="group border-b border-gray-200 dark:border-gray-700 hover:bg-gradient-to-r hover:from-teal-50 hover:to-cyan-50 dark:hover:from-teal-900/20 dark:hover:to-cyan-900/20 transition-all duration-300"
                        style={{
                          animationDelay: `${index * 50}ms`,
                          animation: 'slideUp 0.3s ease-out forwards'
                        }}
                      >
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                              <IoPersonOutline className="text-white text-xl" />
                            </div>
                            <div>
                              <div className="text-sm font-bold text-gray-900 dark:text-white">
                                {ivr.patient_name}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                ID: {ivr.patient.id}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="text-sm font-bold text-gray-900 dark:text-white">
                            {ivr.provider_name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                            {ivr.provider.facility || ivr.provider.email}
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="text-sm font-bold text-gray-900 dark:text-white">
                            {ivr.wound_size_length && ivr.wound_size_width ? (
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex items-center gap-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 px-2 py-1 rounded-lg text-xs font-bold">
                                    📏 {ivr.wound_size_length} × {ivr.wound_size_width} cm
                                  </span>
                                </div>
                                {ivr.wound_size_depth && (
                                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 font-medium">
                                    Depth: {ivr.wound_size_depth} cm
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">Not recorded</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <span className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-black rounded-xl ${getStatusGradient(ivr.status)} group-hover:scale-110 transition-transform duration-300`}>
                            <span className="text-lg">{getStatusIcon(ivr.status)}</span>
                            {ivr.status_display}
                          </span>
                        </td>
                        <td className="px-6 py-5">
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {ivr.submitted_date}
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          {ivr.pdf_url && (
                            <a
                              href={ivr.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl font-bold hover:from-teal-600 hover:to-cyan-700 transform hover:scale-105 transition-all duration-200 shadow-lg"
                            >
                              📄 View PDF
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #14b8a6, #06b6d4);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #0d9488, #0891b2);
        }
      `}</style>
    </div>
  );
};

export default IVRsModal;